import {
    Add,
    DeleteOutline,
    DriveFileRenameOutline,
} from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

interface CustomListDetailHeaderProps {
    listName: string;
    canManage: boolean;
    onDelete: () => void;
    onRename: () => void;
    onAddItems: () => void;
}

export const CustomListDetailHeader = memo(
    ({
        listName,
        canManage,
        onDelete,
        onRename,
        onAddItems,
    }: CustomListDetailHeaderProps) => {
        const { t } = useTranslation();

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '16px',
                    marginBottom: '22px',
                    flexShrink: 0,
                }}
            >
                <Typography
                    variant="h1"
                    data-testclass="custom-list-detail-title"
                >
                    {listName}
                </Typography>
                {canManage ? (
                    <Box sx={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={
                                <DeleteOutline
                                    sx={{ width: '20px', height: '20px' }}
                                />
                            }
                            onClick={onDelete}
                            data-testclass="custom-list-delete"
                            sx={{
                                color: '#848484',
                                borderColor: '#848484',
                                backgroundColor: '#FFFFFF',
                                '&:hover': {
                                    color: '#FF0000',
                                    borderColor: '#FF0000',
                                    backgroundColor: 'rgba(255, 0, 0, 0.05)',
                                },
                            }}
                        >
                            {t('Delete')}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DriveFileRenameOutline />}
                            onClick={onRename}
                            data-testclass="custom-list-rename"
                        >
                            {t('Rename')}
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<Add />}
                            onClick={onAddItems}
                            data-testclass="custom-list-add-items"
                        >
                            {t('customLists.addItems')}
                        </Button>
                    </Box>
                ) : null}
            </Box>
        );
    },
);

CustomListDetailHeader.displayName = 'CustomListDetailHeader';
